import React, { useEffect, useRef, useState } from 'react';

import { PATH_ROUTE_SVG, RouteType } from '@Pages/RetroSynthesis/constants';
import PropTypes from 'prop-types';
import { onFitClick, onCopyRoute } from '@Pages/RetroSynthesis/ProjectRoute/analytics';
import ProjectRouteTreeViewTopPanel from './components/ProjectRouteTreeViewTopPanel';
import ProjectRouteTree from './components/ProjectRouteTree';

// Structure images path from BE response, PATH_ROUTE_SVG is svg image
import { LEAF_PADDING, ORIENTATIONS, ZOOM_ONE } from './constants';
import TrackPageLoadAndStepAutoSet from '../TrackPageLoadAndStepAutoSet';

import './ProjectRouteTreeView.theme.scss';

function ProjectRouteTreeView({
  projectId,
  routeId,
  routeType,
  treeData,
  activeStep,
  setActiveStep,
  conditionsIsShown,
  setConditionsIsShown,
  onDetailsClick,
  onActiveStepChange,
  onPageLoad,
  onStepAutoSetWithDelay,
  onAddNodeClick,
  openLinkOptions,
  copyRoute,
  projectInfo,
}) {
  const treeViewRef = useRef();

  const [zoom, setZoom] = useState(ZOOM_ONE);
  const [orientation, setOrientation] = useState(ORIENTATIONS[0]);
  const [shouldResize, setShouldResize] = useState(false);

  const rotateOnClickHandler = () => {
    const i = ORIENTATIONS.indexOf(orientation);
    const nextOrientation = ORIENTATIONS[i + 1] ? ORIENTATIONS[i + 1] : ORIENTATIONS[0];
    setOrientation(nextOrientation);
  };

  const fitOnClickHandler = () => {
    setShouldResize(true);
    // AA
    onFitClick({ projectId, routeId, routeType, projectInfo });
  };

  const handleResize = () => {
    setShouldResize(false);
  };

  const copyOnClick = () => {
    onCopyRoute({ projectId, routeId, routeType, projectInfo });
    copyRoute(projectId, routeId);
  };

  // reset state when route changes, forcing the tree to be resized
  // without this, tree resizing would only happen if treeData goes val1 --> null --> val2
  // which is not the case when a user switches between two routes with cached data
  useEffect(() => {
    setShouldResize(true);
  }, [projectId, routeId]);

  return (
    <div className="project-route-tree-view flex-1 flex-column-container" ref={treeViewRef}>
      <ProjectRouteTreeViewTopPanel
        projectId={projectId}
        routeId={routeId}
        zoom={zoom}
        zoomOnChange={setZoom}
        rotateOnClick={rotateOnClickHandler}
        fitOnClick={fitOnClickHandler}
        copyOnClick={copyOnClick}
        routeType={routeType}
        treeViewRef={treeViewRef}
      />
      <TrackPageLoadAndStepAutoSet
        activeStep={activeStep}
        conditionsIsShown={conditionsIsShown}
        onPageLoad={onPageLoad}
        onStepAutoSetWithDelay={onStepAutoSetWithDelay}
      />
      {treeData && (
        <ProjectRouteTree
          data={treeData}
          zoom={zoom}
          orientation={orientation}
          onZoomChanged={setZoom}
          structureImagesPath={PATH_ROUTE_SVG}
          activeStep={conditionsIsShown && activeStep}
          setActiveStep={setActiveStep}
          setConditionsIsShown={setConditionsIsShown}
          onDetailsClick={onDetailsClick}
          onActiveStepChange={onActiveStepChange}
          treeId={`${routeId}prt`}
          routeType={routeType}
          addNodeEnabled
          onAddNodeClick={onAddNodeClick}
          openLinkOptions={openLinkOptions}
          shouldResize={shouldResize}
          onResized={handleResize}
          leafPadding={LEAF_PADDING}
        />
      )}
    </div>
  );
}

ProjectRouteTreeView.propTypes = {
  projectId: PropTypes.string,
  routeId: PropTypes.string,
  routeType: PropTypes.oneOf(RouteType.allTypes),
  treeData: PropTypes.object,
  activeStep: PropTypes.string,
  setActiveStep: PropTypes.func,
  conditionsIsShown: PropTypes.bool,
  setConditionsIsShown: PropTypes.func,
  onDetailsClick: PropTypes.func,
  onActiveStepChange: PropTypes.func,
  onPageLoad: PropTypes.func,
  onStepAutoSetWithDelay: PropTypes.func,
  onAddNodeClick: PropTypes.func,
  openLinkOptions: PropTypes.func,
  copyRoute: PropTypes.func,
  projectInfo: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
};

export default ProjectRouteTreeView;
